import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { transactionAPI } from '../services/api';
import {
  ArrowLeft, Plus, DollarSign, Calendar, 
  Trash2, Download, User, Building, FileText
} from 'lucide-react';
import { formatDate, formatCurrency } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useToast } from '../contexts/ToastContext';
import Button from '../components/Button';
import * as XLSX from 'xlsx';

// 민원 종류 상수
const COMPLAINT_TYPES = [
  '각자',
  '개장',
  '개장 및 반출',
  '관리비',
  '구매',
  '리모델링',
  '묘역위치',
  '사용문의',
  '신단지 문의',
  '연고자 정보 변경 및 등록',
  '장례',
  '해약',
  '기타'
];

// 민원 접수 유형 상수
const RECEPTION_TYPES = ['전화', '방문'];

// 민원 상태 상수
const STATUS_OPTIONS = ['pending', 'in_progress', 'completed', 'cancelled'];

const STATUS_LABELS = {
  'pending': '접수',
  'in_progress': '처리중',
  'completed': '완료',
  'cancelled': '취소'
};

const ContractMaintenance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMaintenance, setNewMaintenance] = useState({
    maintenance_type: '기타',
    content: '',
    maintenance_date: new Date().toISOString().split('T')[0],
    cost: 0,
    status: 'pending',
    applicant_name: '',
    applicant_phone: '',
    manager: '',
    reception_type: '전화',
    memo: ''
  });

  // 계약 정보 조회
  const { data: contract, isLoading: isLoadingContract } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => transactionAPI.getById(id),
    select: (response) => response.data,
  });

  // 민원 내역 조회
  const { data: maintenance, isLoading: isLoadingMaintenance } = useQuery({
    queryKey: ['contract-maintenance', id],
    queryFn: () => transactionAPI.getMaintenance(id),
    select: (response) => response.data || [],
  });

  // 민원 추가 뮤테이션
  const addMaintenanceMutation = useMutation({
    mutationFn: (maintenanceData) => transactionAPI.addMaintenance(id, maintenanceData),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-maintenance', id]);
      success('민원이 등록되었습니다.');
      setShowAddForm(false);
      setNewMaintenance({
        maintenance_type: '기타',
        content: '',
        maintenance_date: new Date().toISOString().split('T')[0],
        cost: 0,
        status: 'pending',
        applicant_name: '',
        applicant_phone: '',
        manager: '',
        reception_type: '전화',
        memo: ''
      });
    },
    onError: (error) => {
      showError('민원 등록에 실패했습니다.');
      console.error('민원 등록 실패:', error);
    }
  });

  // 민원 상태 업데이트 뮤테이션
  const updateMaintenanceStatusMutation = useMutation({
    mutationFn: ({ maintenanceId, status }) => transactionAPI.updateMaintenanceStatus(id, maintenanceId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-maintenance', id]);
      success('민원 상태가 업데이트되었습니다.');
    },
    onError: (error) => {
      showError('민원 상태 업데이트에 실패했습니다.');
      console.error('민원 상태 업데이트 실패:', error);
    }
  });

  // 민원 삭제 뮤테이션
  const deleteMaintenanceMutation = useMutation({
    mutationFn: (maintenanceId) => transactionAPI.deleteMaintenance(id, maintenanceId),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract-maintenance', id]);
      success('민원이 삭제되었습니다.');
    },
    onError: (error) => {
      showError('민원 삭제에 실패했습니다.');
      console.error('민원 삭제 실패:', error);
    }
  });

  const handleAddMaintenance = () => {
    if (!newMaintenance.maintenance_type || !newMaintenance.content || !newMaintenance.maintenance_date) {
      showError('필수 항목을 모두 입력해주세요.');
      return;
    }
    addMaintenanceMutation.mutate(newMaintenance);
  };

  const handleStatusChange = (maintenanceId, status) => {
    updateMaintenanceStatusMutation.mutate({ maintenanceId, status });
  };

  const handleDeleteMaintenance = (maintenanceId) => {
    if (window.confirm('정말로 이 민원을 삭제하시겠습니까?')) {
      deleteMaintenanceMutation.mutate(maintenanceId);
    }
  };

  // 엑셀 다운로드
  const handleExcelDownload = () => {
    if (!maintenance || maintenance.length === 0) {
      showError('다운로드할 데이터가 없습니다.');
      return;
    }

    try {
      const excelData = maintenance.map(item => ({
        '민원종류': item.maintenance_type,
        '민원내용': item.content,
        '신청자이름': item.applicant_name || '-',
        '신청자연락처': item.applicant_phone || '-',
        '담당자': item.manager || '-',
        '접수유형': item.reception_type || '-',
        '접수일': formatDate(item.maintenance_date),
        '비용': formatCurrency(item.cost),
        '상태': STATUS_LABELS[item.status] || item.status,
        '메모': item.memo || '',
        '등록일': formatDate(item.created_at)
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '민원내역');

      const fileName = `계약${id}_민원내역_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      success('엑셀 파일이 다운로드되었습니다.');
    } catch (error) {
      console.error('엑셀 다운로드 실패:', error);
      showError('엑셀 다운로드에 실패했습니다.');
    }
  };

  // 권한 확인
  if (!hasPermission(user?.role, 'MAINTENANCE_MANAGE')) {
    return (
      <div className="flex-center min-h-screen">
        <div className="text-center">
          <FileText size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-500">민원 관리 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  if (isLoadingContract || isLoadingMaintenance) {
    return <div className="loading">민원 정보를 불러오는 중...</div>;
  }

  if (!contract) {
    return (
      <div className="error">
        계약 정보를 찾을 수 없습니다.
      </div>
    );
  }

  // 통계 계산
  const totalCost = maintenance?.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0) || 0;
  const completedCount = maintenance?.filter(item => item.status === 'completed').length || 0;
  const pendingCount = maintenance?.filter(item => item.status === 'pending').length || 0;
  const inProgressCount = maintenance?.filter(item => item.status === 'in_progress').length || 0;

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate(`/contracts/${id}`)}
            variant="outline"
            icon={ArrowLeft}
          >
            계약 상세로
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">민원 상세</h1>
            {/* <p className="text-gray-600">계약번호: {contract.id}</p> */}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {hasPermission(user?.role, 'MAINTENANCE_CREATE') && (
            <Button
              onClick={() => setShowAddForm(true)}
              variant="primary"
              icon={Plus}
            >
              민원 등록
            </Button>
          )}
          {maintenance && maintenance.length > 0 && (
            <Button
              onClick={handleExcelDownload}
              variant="outline"
              icon={Download}
            >
              엑셀 다운로드
            </Button>
          )}
        </div>
      </div>

      {/* 계약 정보 요약 */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">계약 정보</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center space-x-2">
              <User size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">사망자:</span>
              <span className="font-medium">{contract.deads?.[0]?.name || '-'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Building size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">묘역:</span>
              <span className="font-medium">
                {contract.mausoleum ? 
                  `${contract.mausoleum.complex}-${contract.mausoleum.area}-${contract.mausoleum.row_number}-${contract.mausoleum.number}` : 
                  '-'
                }
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">계약일:</span>
              <span className="font-medium">{formatDate(contract.contract_date)}</span>
            </div>
            <div className="flex items-center space-x-2">
              <DollarSign size={16} className="text-gray-400" />
              <span className="text-sm text-gray-600">계약 평수:</span>
                                      <span className="font-medium">{contract.mausoleum?.area_size ? `${contract.mausoleum.area_size}평` : '-'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 민원 통계 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card bg-blue-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(totalCost)}
            </div>
            <div className="text-sm text-gray-600">총 민원 비용</div>
          </div>
        </div>
        <div className="card bg-green-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-green-600">
              {completedCount}
            </div>
            <div className="text-sm text-gray-600">완료</div>
          </div>
        </div>
        <div className="card bg-yellow-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {inProgressCount}
            </div>
            <div className="text-sm text-gray-600">처리중</div>
          </div>
        </div>
        <div className="card bg-red-50">
          <div className="card-body text-center">
            <div className="text-2xl font-bold text-red-600">
              {pendingCount}
            </div>
            <div className="text-sm text-gray-600">접수</div>
          </div>
        </div>
      </div>

      {/* 민원 등록 폼 */}
      {showAddForm && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">민원 등록</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">민원 종류 <span className="text-red-500">*</span></label>
                <select
                  value={newMaintenance.maintenance_type}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, maintenance_type: e.target.value }))}
                  className="select"
                >
                  {COMPLAINT_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">민원 접수 유형</label>
                <select
                  value={newMaintenance.reception_type}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, reception_type: e.target.value }))}
                  className="select"
                >
                  {RECEPTION_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">신청자 이름</label>
                <input
                  type="text"
                  value={newMaintenance.applicant_name}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, applicant_name: e.target.value }))}
                  className="input"
                  placeholder="신청자 이름을 입력하세요"
                />
              </div>
              <div>
                <label className="form-label">신청자 연락처</label>
                <input
                  type="text"
                  value={newMaintenance.applicant_phone}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, applicant_phone: e.target.value }))}
                  className="input"
                  placeholder="010-0000-0000"
                />
              </div>
              <div>
                <label className="form-label">담당자</label>
                <input
                  type="text"
                  value={newMaintenance.manager}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, manager: e.target.value }))}
                  className="input"
                  placeholder="담당자명을 입력하세요"
                />
              </div>
              <div>
                <label className="form-label">접수일 <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={newMaintenance.maintenance_date}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, maintenance_date: e.target.value }))}
                  className="input"
                />
              </div>
              <div>
                <label className="form-label">비용</label>
                <input
                  type="number"
                  value={newMaintenance.cost}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, cost: parseInt(e.target.value) || 0 }))}
                  className="input"
                  placeholder="비용을 입력하세요"
                />
              </div>
              <div>
                <label className="form-label">상태</label>
                <select
                  value={newMaintenance.status}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, status: e.target.value }))}
                  className="select"
                >
                  {STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="form-label">민원 내용 <span className="text-red-500">*</span></label>
                <textarea
                  value={newMaintenance.content}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, content: e.target.value }))}
                  className="input"
                  rows="3"
                  placeholder="민원 내용을 상세히 입력하세요"
                />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">메모</label>
                <textarea
                  value={newMaintenance.memo}
                  onChange={(e) => setNewMaintenance(prev => ({ ...prev, memo: e.target.value }))}
                  className="input"
                  rows="2"
                  placeholder="추가 메모를 입력하세요"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setShowAddForm(false)}
                className="btn btn-outline"
              >
                취소
              </button>
              <button
                onClick={handleAddMaintenance}
                disabled={addMaintenanceMutation.isLoading}
                className="btn btn-primary"
              >
                {addMaintenanceMutation.isLoading ? '등록 중...' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 민원 내역 목록 */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">민원 내역</h2>
        </div>
        <div className="card-body">
          {maintenance && maintenance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>민원 종류</th>
                    <th>신청자</th>
                    <th>연락처</th>
                    <th>담당자</th>
                    <th>접수 유형</th>
                    <th>접수일</th>
                    <th>비용</th>
                    <th>상태</th>
                    <th>민원 내용</th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {maintenance.map((item) => (
                    <tr key={item.id}>
                      <td className="font-medium">{item.maintenance_type}</td>
                      <td>{item.applicant_name || '-'}</td>
                      <td>{item.applicant_phone || '-'}</td>
                      <td>{item.manager || '-'}</td>
                      <td>{item.reception_type || '-'}</td>
                      <td>{formatDate(item.maintenance_date)}</td>
                      <td className="font-medium text-left">
                        {formatCurrency(item.cost)}
                      </td>
                      <td>
                        <select
                          value={item.status}
                          onChange={(e) => handleStatusChange(item.id, e.target.value)}
                          className={`select text-sm ${
                            item.status === 'completed' ? 'text-green-600' :
                            item.status === 'in_progress' ? 'text-yellow-600' :
                            item.status === 'cancelled' ? 'text-red-600' :
                            'text-blue-600'
                          }`}
                          disabled={!hasPermission(user?.role, 'MAINTENANCE_EDIT')}
                        >
                          {STATUS_OPTIONS.map(status => (
                            <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="max-w-xs truncate" title={item.content}>
                        {item.content || '-'}
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          {hasPermission(user?.role, 'MAINTENANCE_DELETE') && (
                            <button
                              onClick={() => handleDeleteMaintenance(item.id)}
                              className="text-red-600 hover:text-red-800"
                              title="삭제"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText size={48} className="mx-auto mb-4 text-gray-300" />
              <p>등록된 민원이 없습니다.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractMaintenance; 
